import { CurrencyPipe, TitleCasePipe } from '@angular/common';
import { ProductPricing } from '@galaxy/billing';
import { Pricing, PricingMessageContainer, TieredPrice } from './interface';

export function getPricingMessageContainers(p: Pricing): PricingMessageContainer {
  if (!p) {
    return { pricingMessages: ['Contact Sales'], commitmentMessages: undefined };
  }
  let pricingMessages: string[];
  let tooltipMessages: string[];

  if (p.prices.length > 1) {
    pricingMessages = [buildPricingString(p.prices[0], p.purchaseCostTotal, p.currency, p.frequency)];
    tooltipMessages = (p ? p.prices || [] : []).map((price) => {
      return buildPricingTierString(price, p.currency, p.frequency);
    });
  } else {
    pricingMessages = (p ? p.prices || [] : []).map((price) => {
      return buildPricingTierString(price, p.currency, p.frequency);
    });
  }

  if (p.setupFee > 0) {
    const setupFee = addCurrencyToPricingString(String(p.setupFee / 100), p.currency);
    pricingMessages.push(`${setupFee} Setup fee`);
  }

  const committmentMessages = buildCommitmentMessages(p.frequency, p.initialCommitment, p.recurringCommitment);
  return {
    pricingMessages: pricingMessages,
    tooltipMessages: tooltipMessages,
    commitmentMessages: committmentMessages,
  };
}

export function formatBillingFrequency(value?: string): string {
  let returnValue = '';
  if (value) {
    switch (value.toLowerCase()) {
      case 'once':
      case 'one time':
      case 'one_time':
        returnValue = 'One Time';
        break;
      default:
        returnValue = new TitleCasePipe().transform(value);
    }
  }
  return returnValue;
}

export function isValidRange(min: number, max: number): boolean {
  const defined = min && max;
  const outOfBounds = max <= 0 && min <= 0;
  const isOneToInfinity = max === -1 && min === 1;
  if (!defined || outOfBounds || isOneToInfinity) {
    return false;
  }
  return true;
}

function isValidPrice(tier: TieredPrice): boolean {
  return Boolean(tier && (tier.price || tier.override));
}

export function buildPricingTierString(tier: TieredPrice, currency: string, frequency: string): string {
  let pricingString = '';
  if (!(isValidPrice(tier) && currency && frequency)) {
    return 'Contact Sales';
  }

  if (tier.override) {
    if (tier.override === 'FREE') {
      pricingString = 'Free';
    } else {
      pricingString = 'Contact Sales';
    }
  } else {
    pricingString = addCurrencyToPricingString(String(tier.price / 100), currency);
    if (frequency) {
      pricingString = pricingString + ' ' + formatBillingFrequency(frequency);
    }
  }

  if (isValidRange(tier.quantityMin, tier.quantityMax)) {
    const quantityMax = tier.quantityMax && tier.quantityMax >= 0 ? String(tier.quantityMax) : '∞';
    const quantityMin = tier.quantityMin && tier.quantityMin >= 0 ? String(tier.quantityMin) : '0';
    const tierRange = `${quantityMin} to ${quantityMax}`;
    pricingString = `${tierRange} ${pricingString}`;
  }
  if (tier.isStartingPrice) {
    pricingString = `Starting at ${pricingString}`;
  }

  return pricingString;
}

export function buildPricingString(
  tier: TieredPrice,
  purchaseCost: number,
  currency: string,
  frequency: string,
): string {
  let pricingString = '';

  if (!(isValidPrice(tier) && currency && frequency)) {
    return 'Contact Sales';
  }

  if (tier.override && !purchaseCost) {
    if (tier.override === 'FREE') {
      pricingString = 'Free';
      return pricingString;
    } else {
      pricingString = 'Contact Sales';
      return pricingString;
    }
  }

  if (!purchaseCost) {
    pricingString = 'Contact Sales';
    return pricingString;
  }

  pricingString = addCurrencyToPricingString(String(purchaseCost / 100), currency);
  if (frequency) {
    pricingString = pricingString + ' ' + formatBillingFrequency(frequency);
  }
  return pricingString;
}

function addCurrencyToPricingString(priceString: string, currency: string): string {
  if (!priceString) {
    return '';
  }
  if (!currency) {
    return priceString;
  }
  return new CurrencyPipe('en-US').transform(priceString, currency, 'symbol', '1.2-2');
}

function formatBillingFrequencyForCommitment(frequency: string, num: number): string {
  switch (frequency.toLowerCase()) {
    case 'monthly':
      return 'month';
    case 'yearly':
      return 'year';
    case 'one time':
    case 'once':
    case 'one_time':
      return num <= 1 ? 'one time' : 'times';
  }
  return frequency;
}

export function buildCommitmentMessages(
  frequency: string,
  initialCommitment?: number,
  recurringCommitment?: number,
): string[] {
  frequency = formatBillingFrequency(frequency);
  const hasInitialCommitment = initialCommitment && initialCommitment > 1;
  const hasRecurringCommitment = recurringCommitment && recurringCommitment > 1;
  const msgs: string[] = [];

  if (hasInitialCommitment) {
    const initialFrequency = formatBillingFrequencyForCommitment(frequency, initialCommitment);
    msgs.push(`Initial ${initialCommitment} ${initialFrequency} commitment`);
  }
  if (hasRecurringCommitment) {
    const recurringFrequency = formatBillingFrequencyForCommitment(frequency, recurringCommitment);
    msgs.push(`Recurring ${recurringCommitment} ${recurringFrequency} commitment`);
  }
  return msgs;
}

export function convertPricingToTablePricing(pricing: ProductPricing, purchaseCostTotal?: number): Pricing {
  if (!pricing) {
    return undefined;
  }

  const convertedPricing = {
    currency: pricing.currency,
    frequency: pricing.frequency,
    purchaseCostTotal: purchaseCostTotal,
    setupFee: pricing.setupFee,
  } as Pricing;

  if (pricing.commitment) {
    convertedPricing.initialCommitment = pricing.commitment.initial;
    convertedPricing.recurringCommitment = pricing.commitment.recurring;
  }

  convertedPricing.prices = (pricing.pricingRules || []).map((p) => {
    const tieredPricing = {
      price: p.price,
      quantityMin: p.minUnits,
      quantityMax: p.maxUnits,
      isStartingPrice: pricing.isStartingPrice,
    } as TieredPrice;

    if (!p.price) {
      tieredPricing.override = 'FREE';
    } else if (p.price < 0) {
      tieredPricing.override = 'CONTACT SALES';
    }
    return tieredPricing;
  });

  return convertedPricing;
}
